import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, LayoutGrid, Building2, Calendar as CalIcon, FileText,
  Mail, MessageSquare, TrendingUp, LogOut, RefreshCw, Plus,
  Trash2, ExternalLink, ShieldAlert, Send, ChevronLeft, ChevronRight, Search,
} from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import Seo from "../../components/Seo";

const STAGES = ["New", "Qualified", "Nurture", "Under Contract", "Closed Won", "Lost"];
const CONTACT_TYPES = ["buyer", "seller", "borrower", "renter", "past_client", "sphere"];
const EVENT_TYPES = ["showing", "closing", "listing_appt", "follow_up", "call", "meeting"];

const stageColor = {
  "New": "bg-blue-50 text-blue-700 border-blue-200",
  "Qualified": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Nurture": "bg-amber-50 text-amber-700 border-amber-200",
  "Under Contract": "bg-purple-50 text-purple-700 border-purple-200",
  "Closed Won": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Lost": "bg-gray-50 text-gray-600 border-gray-200",
};

const fmt = (n) => (n ? `$${Number(n).toLocaleString()}` : "—");

export default function Crm() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const [tab, setTab] = useState("dashboard");
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);

  const effectiveUser = user || location.state?.user;

  useEffect(() => {
    if (!effectiveUser) return;
    api.get("/crm/me", { withCredentials: true }).then((r) => setMe(r.data)).catch(() => setMe(null));
    api.get("/crm/stats", { withCredentials: true }).then((r) => setStats(r.data)).catch(() => setStats(null));
  }, [effectiveUser, tab]);

  if (loading) return <div className="py-32 text-center text-gray-500">Loading…</div>;
  if (!effectiveUser) return <Navigate to="/admin/login" replace />;
  if (!me && effectiveUser) {
    // Wait for role check
    return (
      <div className="py-32 text-center text-gray-500 flex items-center justify-center gap-3">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading CRM…
      </div>
    );
  }

  if (me && !["admin", "agent", "loan_officer", "assistant"].includes(me.role)) {
    return (
      <div className="max-w-lg mx-auto mt-24 p-10 bg-white border border-gray-200 rounded-xl text-center" data-testid="crm-forbidden">
        <ShieldAlert className="w-10 h-10 text-amber-500 mx-auto mb-4" />
        <div className="font-display text-2xl font-semibold mb-2">Not authorized</div>
        <p className="text-sm text-gray-500">
          Your account <span className="font-medium">{effectiveUser.email}</span> doesn&apos;t have a CRM role yet.
          Ask an admin to invite you.
        </p>
        <Link to="/admin" className="mt-6 inline-block text-[#E63946] hover:underline text-sm font-semibold">
          Back to Admin
        </Link>
      </div>
    );
  }

  const NAV = [
    { key: "dashboard", label: "Overview", icon: TrendingUp },
    { key: "pipeline", label: "Pipeline", icon: LayoutGrid },
    { key: "contacts", label: "Contacts", icon: Users },
    { key: "listings", label: "MLS Listings", icon: Building2 },
    { key: "calendar", label: "Calendar", icon: CalIcon },
    { key: "documents", label: "Documents", icon: FileText },
    { key: "team", label: "Team", icon: Users, adminOnly: true },
  ];

  return (
    <div className="bg-stone-50 min-h-screen" data-testid="crm-root">
      <Seo title="CRM" noindex path="/crm" />

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#E63946]">Gens Realty · Edge Home Finance</div>
            <h1 className="font-display text-3xl font-light tracking-tighter">CRM</h1>
            <div className="text-xs text-gray-500 mt-1">
              {me?.name} · <span className="uppercase tracking-wide">{me?.role}</span> · {me?.department}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-xs uppercase tracking-[0.15em] text-gray-500 hover:text-[#E63946]" data-testid="crm-back-admin">
              ← Admin
            </Link>
            <button onClick={logout} className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#E63946]" data-testid="crm-logout">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Sidebar */}
        <aside className="lg:col-span-2">
          <nav className="bg-white border border-gray-200 rounded-xl p-3 space-y-1 sticky top-6" data-testid="crm-sidebar">
            {NAV.filter((n) => !n.adminOnly || me?.role === "admin").map((n) => {
              const Icon = n.icon;
              const active = tab === n.key;
              return (
                <button
                  key={n.key}
                  onClick={() => setTab(n.key)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                    active ? "bg-[#E63946]/10 text-[#E63946] font-semibold" : "text-[#1A1A1A] hover:bg-stone-50"
                  }`}
                  data-testid={`crm-nav-${n.key}`}
                >
                  <Icon className="w-4 h-4" />
                  {n.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Content */}
        <main className="lg:col-span-10 space-y-6">
          {tab === "dashboard" && <DashboardView stats={stats} setTab={setTab} />}
          {tab === "pipeline" && <PipelineView role={me?.role} />}
          {tab === "contacts" && <ContactsView role={me?.role} />}
          {tab === "listings" && <ListingsView role={me?.role} />}
          {tab === "calendar" && <CalendarView role={me?.role} />}
          {tab === "documents" && <DocumentsView role={me?.role} />}
          {tab === "team" && me?.role === "admin" && <TeamView />}
        </main>
      </div>
    </div>
  );
}

// ---------- Dashboard ----------
function DashboardView({ stats, setTab }) {
  if (!stats) return <div className="text-gray-500">Loading stats…</div>;
  const cards = [
    { label: "Contacts", value: stats.contacts_total, key: "contacts" },
    { label: "Active Listings", value: stats.listings_active, key: "listings" },
    { label: "Upcoming Events", value: stats.events_upcoming, key: "calendar" },
    { label: "Team", value: stats.team_size, key: "team" },
    { label: "Comms (30d)", value: stats.communications_30d, key: "contacts" },
    { label: "In Nurture", value: stats.contacts_by_stage["Nurture"] || 0, key: "pipeline" },
  ];
  return (
    <div className="space-y-6" data-testid="crm-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {cards.map((c) => (
          <button
            key={c.label}
            onClick={() => setTab(c.key)}
            className="bg-white border border-gray-200 rounded-xl p-5 text-left hover:border-[#E63946] transition-colors"
          >
            <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">{c.label}</div>
            <div className="font-display text-3xl font-semibold mt-2">{c.value}</div>
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-3">Pipeline snapshot</div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {STAGES.map((s) => (
            <div key={s} className={`border rounded-lg p-3 ${stageColor[s]}`}>
              <div className="text-[10px] uppercase tracking-[0.15em] opacity-70">{s}</div>
              <div className="font-display text-2xl font-semibold mt-1">
                {stats.contacts_by_stage[s] || 0}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Pipeline (Kanban) ----------
function PipelineView({ role }) {
  const [board, setBoard] = useState({});
  const [dragging, setDragging] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/crm/pipeline", { withCredentials: true });
      setBoard(r.data.board);
    } catch { toast.error("Failed to load pipeline"); }
  };
  useEffect(() => { load(); }, []);

  const canEdit = ["admin", "agent", "loan_officer"].includes(role);

  const moveStage = async (contact, newStage) => {
    if (!canEdit || contact.stage === newStage) return;
    try {
      await api.patch(`/crm/contacts/${contact.id}/stage`, { stage: newStage }, { withCredentials: true });
      toast.success(`Moved to ${newStage}`);
      load();
    } catch { toast.error("Failed to move"); }
  };

  return (
    <div data-testid="crm-pipeline">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl font-semibold">Pipeline</h2>
        <button onClick={load} className="text-xs uppercase tracking-[0.15em] text-gray-500 hover:text-[#E63946]" data-testid="crm-pipeline-refresh">
          <RefreshCw className="w-3.5 h-3.5 inline mr-1" /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 overflow-x-auto">
        {STAGES.map((s) => (
          <div
            key={s}
            className={`rounded-xl border ${stageColor[s]} p-3 min-h-[400px]`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => dragging && moveStage(dragging, s)}
            data-testid={`crm-stage-${s.replace(/\s+/g, '-').toLowerCase()}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.15em] font-semibold">{s}</div>
              <div className="text-xs opacity-70">{(board[s] || []).length}</div>
            </div>
            <div className="space-y-2">
              {(board[s] || []).map((c) => (
                <div
                  key={c.id}
                  draggable={canEdit}
                  onDragStart={() => setDragging(c)}
                  onDragEnd={() => setDragging(null)}
                  className="bg-white border border-gray-200 rounded-lg p-3 cursor-grab active:cursor-grabbing text-xs"
                  data-testid={`crm-card-${c.id}`}
                >
                  <div className="font-semibold text-[#1A1A1A]">{c.name}</div>
                  <div className="text-gray-500 mt-0.5 text-[11px]">{c.contact_type} · {c.email || c.phone || "—"}</div>
                  {c.budget_min || c.budget_max ? (
                    <div className="text-[10px] text-gray-500 mt-1">
                      {fmt(c.budget_min)} – {fmt(c.budget_max)}
                    </div>
                  ) : null}
                  {c.neighborhoods?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {c.neighborhoods.slice(0, 2).map((n) => (
                        <span key={n} className="text-[9px] px-1.5 py-0.5 rounded bg-stone-100 text-[#1A1A1A]">{n}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Contacts ----------
function ContactsView({ role }) {
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [filter, setFilter] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/crm/contacts", { withCredentials: true });
      setContacts(r.data);
    } catch { toast.error("Failed to load contacts"); }
  };
  useEffect(() => { load(); }, []);

  const canDelete = ["admin", "agent", "loan_officer"].includes(role);
  const canEdit = ["admin", "agent", "loan_officer"].includes(role);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) =>
      c.name.toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.phone || "").toLowerCase().includes(q) ||
      (c.stage || "").toLowerCase().includes(q)
    );
  }, [contacts, filter]);

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"?`)) return;
    try {
      await api.delete(`/crm/contacts/${c.id}`, { withCredentials: true });
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div data-testid="crm-contacts">
      <div className="flex items-center justify-between mb-4 gap-3">
        <h2 className="font-display text-2xl font-semibold">Contacts</h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search contacts…"
              className="pl-8 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#E63946]"
              data-testid="crm-contacts-filter"
            />
          </div>
          {canEdit && (
            <button onClick={() => setFormOpen(true)} className="bg-[#E63946] hover:bg-[#c9313d] text-white px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2" data-testid="crm-new-contact">
              <Plus className="w-4 h-4" /> New
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-100 text-[10px] uppercase tracking-[0.18em] text-gray-500">
            <tr>
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Type</th>
              <th className="text-left px-5 py-3">Stage</th>
              <th className="text-left px-5 py-3">Contact</th>
              <th className="text-left px-5 py-3">Budget</th>
              <th className="text-left px-5 py-3">Source</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map((c) => (
              <tr key={c.id} className="hover:bg-stone-50">
                <td className="px-5 py-3 font-medium">{c.name}</td>
                <td className="px-5 py-3 text-gray-500">{c.contact_type}</td>
                <td className="px-5 py-3">
                  <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded border ${stageColor[c.stage] || ''}`}>{c.stage}</span>
                </td>
                <td className="px-5 py-3 text-gray-600 text-xs">
                  {c.email && <div>{c.email}</div>}
                  {c.phone && <div>{c.phone}</div>}
                </td>
                <td className="px-5 py-3 text-xs text-gray-600">
                  {c.budget_min || c.budget_max ? `${fmt(c.budget_min)} – ${fmt(c.budget_max)}` : "—"}
                </td>
                <td className="px-5 py-3 text-xs text-gray-500">{c.source || "—"}</td>
                <td className="px-5 py-3 text-right space-x-2">
                  <button onClick={() => setSelected(c)} className="text-[#E63946] hover:underline text-xs" data-testid={`crm-view-${c.id}`}>Open</button>
                  {canDelete && (
                    <button onClick={() => remove(c)} className="text-gray-400 hover:text-red-600 inline-flex" data-testid={`crm-del-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-gray-500">No contacts yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {formOpen && (
        <ContactForm
          onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); load(); }}
        />
      )}
      {selected && (
        <ContactDrawer
          contact={selected}
          onClose={() => setSelected(null)}
          onChanged={load}
          role={role}
        />
      )}
    </div>
  );
}

function ContactForm({ onClose, onSaved }) {
  const [f, setF] = useState({
    name: "", email: "", phone: "", contact_type: "buyer", stage: "New",
    source: "", notes: "", budget_min: "", budget_max: "", neighborhoods: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!f.name) return toast.error("Name required");
    setSaving(true);
    try {
      await api.post("/crm/contacts", {
        ...f,
        budget_min: f.budget_min ? Number(f.budget_min) : null,
        budget_max: f.budget_max ? Number(f.budget_max) : null,
        neighborhoods: f.neighborhoods ? f.neighborhoods.split(",").map((s) => s.trim()).filter(Boolean) : [],
        email: f.email || null,
        phone: f.phone || null,
      }, { withCredentials: true });
      toast.success("Contact created");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg p-8 space-y-4" data-testid="crm-contact-form">
        <div className="font-display text-2xl font-semibold">New contact</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name*" value={f.name} onChange={(v) => setF({ ...f, name: v })} testId="cf-name" />
          <Select label="Type" value={f.contact_type} onChange={(v) => setF({ ...f, contact_type: v })} options={CONTACT_TYPES} testId="cf-type" />
          <Field label="Email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testId="cf-email" />
          <Field label="Phone" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testId="cf-phone" />
          <Select label="Stage" value={f.stage} onChange={(v) => setF({ ...f, stage: v })} options={STAGES} testId="cf-stage" />
          <Field label="Source" value={f.source} onChange={(v) => setF({ ...f, source: v })} testId="cf-source" />
          <Field label="Budget min" type="number" value={f.budget_min} onChange={(v) => setF({ ...f, budget_min: v })} testId="cf-bmin" />
          <Field label="Budget max" type="number" value={f.budget_max} onChange={(v) => setF({ ...f, budget_max: v })} testId="cf-bmax" />
        </div>
        <Field label="Neighborhoods (comma)" value={f.neighborhoods} onChange={(v) => setF({ ...f, neighborhoods: v })} testId="cf-nb" />
        <TextArea label="Notes" value={f.notes} onChange={(v) => setF({ ...f, notes: v })} testId="cf-notes" />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-[#1A1A1A]">Cancel</button>
          <button type="submit" disabled={saving} className="bg-[#E63946] hover:bg-[#c9313d] text-white px-5 py-2 rounded-md font-semibold text-sm disabled:opacity-60" data-testid="cf-submit">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ContactDrawer({ contact, onClose, onChanged, role }) {
  const [comms, setComms] = useState([]);
  const [docs, setDocs] = useState([]);
  const [emailBody, setEmailBody] = useState({ subject: "", body: "" });
  const [smsBody, setSmsBody] = useState("");
  const [sending, setSending] = useState(false);
  const canSend = ["admin", "agent", "loan_officer", "assistant"].includes(role);

  useEffect(() => {
    api.get(`/crm/communications?contact_id=${contact.id}`, { withCredentials: true }).then((r) => setComms(r.data)).catch(() => {});
    api.get(`/crm/documents?contact_id=${contact.id}`, { withCredentials: true }).then((r) => setDocs(r.data)).catch(() => {});
  }, [contact.id]);

  const sendEmail = async () => {
    if (!contact.email) return toast.error("Contact has no email");
    if (!emailBody.subject || !emailBody.body) return toast.error("Subject + body required");
    setSending(true);
    try {
      const r = await api.post("/crm/email", {
        to: contact.email, subject: emailBody.subject, body: emailBody.body, contact_id: contact.id
      }, { withCredentials: true });
      toast.success(r.data.provider_enabled ? "Email sent via SendGrid" : "Email queued (mocked — add SENDGRID_API_KEY to send for real)");
      setEmailBody({ subject: "", body: "" });
      const rr = await api.get(`/crm/communications?contact_id=${contact.id}`, { withCredentials: true });
      setComms(rr.data);
    } catch { toast.error("Send failed"); }
    finally { setSending(false); }
  };

  const sendSms = async () => {
    if (!contact.phone) return toast.error("Contact has no phone");
    if (!smsBody) return toast.error("Message required");
    setSending(true);
    try {
      const r = await api.post("/crm/sms", { to: contact.phone, body: smsBody, contact_id: contact.id }, { withCredentials: true });
      toast.success(r.data.provider_enabled ? "SMS sent via Twilio" : "SMS queued (mocked — add TWILIO_ACCOUNT_SID to send for real)");
      setSmsBody("");
      const rr = await api.get(`/crm/communications?contact_id=${contact.id}`, { withCredentials: true });
      setComms(rr.data);
    } catch { toast.error("Send failed"); }
    finally { setSending(false); }
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("contact_id", contact.id);
    try {
      await api.post("/crm/documents", fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      const r = await api.get(`/crm/documents?contact_id=${contact.id}`, { withCredentials: true });
      setDocs(r.data);
    } catch { toast.error("Upload failed"); }
    e.target.value = "";
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="crm-contact-drawer">
        <div className="p-6 border-b border-gray-200 flex justify-between items-start">
          <div>
            <div className="font-display text-2xl font-semibold">{contact.name}</div>
            <div className="text-sm text-gray-500 mt-1">
              {contact.contact_type} · <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded border ${stageColor[contact.stage]}`}>{contact.stage}</span>
            </div>
            <div className="mt-2 text-xs text-gray-600 space-y-0.5">
              {contact.email && <div>{contact.email}</div>}
              {contact.phone && <div>{contact.phone}</div>}
              {(contact.budget_min || contact.budget_max) && <div>Budget: {fmt(contact.budget_min)} – {fmt(contact.budget_max)}</div>}
              {contact.source && <div>Source: {contact.source}</div>}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>

        {contact.notes && (
          <div className="p-6 border-b border-gray-100">
            <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-2">Notes</div>
            <p className="text-sm text-[#333] whitespace-pre-line">{contact.notes}</p>
          </div>
        )}

        {/* Send email */}
        {canSend && contact.email && (
          <div className="p-6 border-b border-gray-100">
            <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-3 flex items-center gap-1"><Mail className="w-3 h-3" /> Send email</div>
            <div className="space-y-2">
              <input value={emailBody.subject} onChange={(e) => setEmailBody({ ...emailBody, subject: e.target.value })} placeholder="Subject" className="w-full px-3 py-2 bg-stone-50 border border-gray-200 rounded-lg text-sm" data-testid="crm-email-subject" />
              <textarea value={emailBody.body} onChange={(e) => setEmailBody({ ...emailBody, body: e.target.value })} placeholder="Message (HTML supported)" rows={4} className="w-full px-3 py-2 bg-stone-50 border border-gray-200 rounded-lg text-sm" data-testid="crm-email-body" />
              <button onClick={sendEmail} disabled={sending} className="bg-[#E63946] hover:bg-[#c9313d] text-white px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="crm-email-send">
                <Send className="w-3.5 h-3.5" /> Send
              </button>
            </div>
          </div>
        )}

        {/* Send SMS */}
        {canSend && contact.phone && (
          <div className="p-6 border-b border-gray-100">
            <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-3 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Send SMS</div>
            <div className="space-y-2">
              <textarea value={smsBody} onChange={(e) => setSmsBody(e.target.value)} placeholder="Text message" rows={2} maxLength={160} className="w-full px-3 py-2 bg-stone-50 border border-gray-200 rounded-lg text-sm" data-testid="crm-sms-body" />
              <div className="flex items-center justify-between">
                <div className="text-[10px] text-gray-400">{smsBody.length}/160</div>
                <button onClick={sendSms} disabled={sending} className="bg-[#E63946] hover:bg-[#c9313d] text-white px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="crm-sms-send">
                  <Send className="w-3.5 h-3.5" /> Send SMS
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Documents */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex justify-between items-center mb-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 flex items-center gap-1"><FileText className="w-3 h-3" /> Documents</div>
            {canSend && (
              <label className="text-xs text-[#E63946] hover:underline cursor-pointer" data-testid="crm-doc-upload">
                + Upload
                <input type="file" onChange={uploadDoc} className="hidden" />
              </label>
            )}
          </div>
          <div className="space-y-1.5">
            {docs.map((d) => (
              <a key={d.id} href={`${process.env.REACT_APP_BACKEND_URL}/api/crm/documents/${d.id}`} target="_blank" rel="noopener noreferrer" className="flex justify-between text-xs px-3 py-2 rounded-lg bg-stone-50 hover:bg-stone-100">
                <span>{d.filename}</span>
                <span className="text-gray-400">{Math.ceil((d.size || 0) / 1024)} KB</span>
              </a>
            ))}
            {docs.length === 0 && <div className="text-xs text-gray-400 text-center py-4">No documents yet</div>}
          </div>
        </div>

        {/* Activity */}
        <div className="p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-3">Activity</div>
          <div className="space-y-2">
            {comms.map((c) => (
              <div key={c.id} className="text-xs px-3 py-2 rounded-lg bg-stone-50 border border-gray-100">
                <div className="flex justify-between">
                  <span className="font-semibold">
                    {c.channel === "email" ? "📧" : "💬"} {c.channel.toUpperCase()} → {c.to_address}
                    {c.status === "mocked" && <span className="ml-2 text-[9px] text-amber-600">MOCKED</span>}
                  </span>
                  <span className="text-gray-400">{new Date(c.created_at).toLocaleString()}</span>
                </div>
                {c.subject && <div className="text-gray-600 mt-0.5">{c.subject}</div>}
                <div className="text-gray-600 mt-0.5 whitespace-pre-line line-clamp-3">{c.body}</div>
              </div>
            ))}
            {comms.length === 0 && <div className="text-xs text-gray-400 text-center py-4">No activity yet</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Listings ----------
function ListingsView({ role }) {
  const [listings, setListings] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastLoaded, setLastLoaded] = useState(null);
  const [filter, setFilter] = useState("Active");
  const [providerStatus, setProviderStatus] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);

  const load = async ({ manual = false } = {}) => {
    if (manual) setRefreshing(true);
    try {
      const q = filter ? `?status=${encodeURIComponent(filter)}` : "";
      const r = await api.get(`/crm/listings${q}`, { withCredentials: true });
      setListings(r.data);
      setLastLoaded(new Date());
      if (manual) toast.success(`Refreshed · ${r.data.length} listings`);
    } catch {
      toast.error("Failed to load listings");
    } finally {
      if (manual) setRefreshing(false);
    }
  };
  useEffect(() => { load(); }, [filter]);
  useEffect(() => {
    api.get("/crm/listings/provider-status", { withCredentials: true })
      .then((r) => setProviderStatus(r.data))
      .catch(() => setProviderStatus(null));
  }, []);

  // Poll sync status every 5s while syncing (or briefly after)
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get("/crm/listings/sync-status", { withCredentials: true });
        if (!cancelled) {
          setSyncStatus(r.data);
          if (r.data?.status === "running") {
            setTimeout(poll, 5000);
          } else if (syncing) {
            setSyncing(false);
            load();
            if (r.data?.status === "idle") toast.success(`Sync complete — ${r.data.last_synced} listings`);
            else if (r.data?.status === "error") toast.error(`Sync failed: ${r.data.last_error}`);
          }
        }
      } catch { /* ignore */ }
    };
    poll();
    return () => { cancelled = true; };
  }, [syncing]);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/crm/listings/sync", {}, { withCredentials: true });
      if (r.data.status === "started") {
        toast.success(`Trestle sync started in background — this may take 3–10 minutes`);
      } else if (r.data.status === "complete") {
        toast.success(`Synced ${r.data.synced} listings from ${r.data.provider}`);
        load();
        setSyncing(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
      setSyncing(false);
    }
  };

  const canSync = ["admin", "agent"].includes(role);

  return (
    <div data-testid="crm-listings">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-baseline gap-3">
          <h2 className="font-display text-2xl font-semibold">MLS Listings</h2>
          {lastLoaded && (
            <span className="inline-flex items-center gap-1 text-[11px] text-gray-500" data-testid="crm-listings-last-loaded">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Loaded {lastLoaded.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm" data-testid="crm-listings-filter">
            <option value="">All statuses</option>
            <option value="Active">Active</option>
            <option value="Pending">Pending</option>
            <option value="Sold">Sold</option>
          </select>
          <button
            onClick={() => load({ manual: true })}
            disabled={refreshing}
            className="bg-white border border-gray-200 hover:border-[#E63946] hover:text-[#E63946] text-[#1A1A1A] px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2 disabled:opacity-60"
            data-testid="crm-listings-refresh"
            title="Re-fetch the currently cached listings from the database (does not re-pull from Trestle)"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh now"}
          </button>
          {canSync && (
            <button onClick={sync} disabled={syncing} className="bg-[#1A1A1A] hover:bg-[#333] text-white px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="crm-listings-sync" title="Trigger a full pull from CoreLogic Trestle (may take 3–10 minutes)">
              <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> Sync from Trestle
            </button>
          )}
        </div>
      </div>
      <div className={`text-xs rounded-lg p-3 mb-4 border ${
        providerStatus?.provider === "trestle" && providerStatus?.configured
          ? "text-emerald-700 bg-emerald-50 border-emerald-200"
          : providerStatus?.provider === "trestle"
          ? "text-red-700 bg-red-50 border-red-200"
          : "text-amber-700 bg-amber-50 border-amber-200"
      }`}>
        {providerStatus?.provider === "trestle" && providerStatus?.configured && (
          <>
            <strong>Trestle Web API connected.</strong> Pulling live listings from CoreLogic Trestle.
            {providerStatus.integration_id && <span className="ml-2 opacity-70">({providerStatus.integration_id})</span>}
          </>
        )}
        {providerStatus?.provider === "trestle" && !providerStatus?.configured && (
          <>
            <strong>Trestle selected but not configured.</strong> Set <code>TRESTLE_CLIENT_ID</code> and{" "}
            <code>TRESTLE_CLIENT_SECRET</code> in <code>backend/.env</code> (values come from your Trestle Product Manager).
          </>
        )}
        {(!providerStatus || providerStatus.provider === "mock") && (
          <>
            <strong>Mock feed active.</strong> To pull real HAR listings via Trestle, set <code>MLS_PROVIDER=trestle</code>{" "}
            + Trestle credentials in <code>backend/.env</code>.
          </>
        )}
      </div>
      {syncStatus && (syncStatus.status === "running" || syncStatus.last_run) && providerStatus?.provider === "trestle" && (
        <div className="text-xs bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-blue-800">
          {syncStatus.status === "running" && <><RefreshCw className="w-3 h-3 inline mr-1 animate-spin" /> Sync in progress — {syncStatus.listings_in_db || 0} listings ingested so far.</>}
          {syncStatus.status === "idle" && <>Last sync: {new Date(syncStatus.last_run).toLocaleString()} · {syncStatus.last_synced} listings · {syncStatus.listings_in_db} in DB</>}
          {syncStatus.status === "error" && <span className="text-red-700">Last sync failed: {syncStatus.last_error}</span>}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {listings.map((l) => (
          <div key={l.listing_key} className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`crm-listing-${l.listing_key}`}>
            {l.photos?.[0] && <img src={l.photos[0]} alt={l.address} className="w-full h-40 object-cover" />}
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="font-display text-xl font-semibold">${l.price.toLocaleString()}</div>
                <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded ${l.status === 'Active' ? 'bg-emerald-50 text-emerald-700' : l.status === 'Pending' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>{l.status}</span>
              </div>
              <div className="text-sm text-[#333] mt-1">{l.address}</div>
              <div className="text-xs text-gray-500">{l.city}, {l.state} {l.zip}</div>
              <div className="text-xs text-gray-600 mt-2">
                {l.beds} bed · {l.baths} bath · {l.sqft.toLocaleString()} sqft · Built {l.year_built}
              </div>
              <div className="text-[10px] text-gray-400 mt-1">
                {l.list_office} · {l.days_on_market} DOM · {l.listing_key}
              </div>
            </div>
          </div>
        ))}
        {listings.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-500">No listings. Click &quot;Sync feed&quot;.</div>
        )}
      </div>
    </div>
  );
}

// ---------- Calendar ----------
function CalendarView({ role }) {
  const [events, setEvents] = useState([]);
  const [monthOffset, setMonthOffset] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const canEdit = ["admin", "agent", "loan_officer"].includes(role);

  const anchor = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + monthOffset);
    d.setDate(1);
    return d;
  }, [monthOffset]);

  const load = async () => {
    const start = new Date(anchor); start.setDate(1);
    const end = new Date(anchor); end.setMonth(end.getMonth() + 1); end.setDate(0); end.setHours(23, 59, 59);
    try {
      const r = await api.get(`/crm/events?start=${start.toISOString()}&end=${end.toISOString()}`, { withCredentials: true });
      setEvents(r.data);
    } catch { toast.error("Failed to load calendar"); }
  };
  useEffect(() => { load(); }, [monthOffset]);

  const remove = async (id) => {
    if (!window.confirm("Delete event?")) return;
    try { await api.delete(`/crm/events/${id}`, { withCredentials: true }); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const eventsByDay = useMemo(() => {
    const m = {};
    events.forEach((e) => {
      const key = new Date(e.start).toDateString();
      (m[key] = m[key] || []).push(e);
    });
    return m;
  }, [events]);

  const daysInMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
  const firstWeekday = anchor.getDay();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(anchor.getFullYear(), anchor.getMonth(), d));

  return (
    <div data-testid="crm-calendar">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-2xl font-semibold">Calendar</h2>
          <div className="flex items-center gap-1">
            <button onClick={() => setMonthOffset((n) => n - 1)} className="p-1.5 rounded hover:bg-stone-100" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
            <div className="text-sm font-medium min-w-[130px] text-center">
              {anchor.toLocaleString('en-US', { month: 'long', year: 'numeric' })}
            </div>
            <button onClick={() => setMonthOffset((n) => n + 1)} className="p-1.5 rounded hover:bg-stone-100" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
        {canEdit && (
          <button onClick={() => setFormOpen(true)} className="bg-[#E63946] hover:bg-[#c9313d] text-white px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2" data-testid="crm-new-event">
            <Plus className="w-4 h-4" /> New event
          </button>
        )}
      </div>

      {/* Month grid */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-7 text-[10px] uppercase tracking-[0.15em] text-gray-500 bg-stone-50 border-b">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d} className="px-2 py-2 text-center">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d, i) => {
            const ev = d ? (eventsByDay[d.toDateString()] || []) : [];
            const isToday = d && d.toDateString() === new Date().toDateString();
            return (
              <div key={i} className="border-b border-r border-gray-100 min-h-[90px] p-1.5 text-xs">
                {d && (
                  <>
                    <div className={`text-[11px] font-semibold mb-1 ${isToday ? 'text-[#E63946]' : 'text-gray-500'}`}>{d.getDate()}</div>
                    {ev.slice(0, 3).map((e) => (
                      <div key={e.id} className="mb-1 px-1.5 py-0.5 rounded bg-red-50 border border-red-100 truncate text-[10px]" title={`${e.title} · ${new Date(e.start).toLocaleTimeString([], {hour:'numeric',minute:'2-digit'})}`}>
                        {new Date(e.start).toLocaleTimeString([], {hour:'numeric'})} · {e.title}
                      </div>
                    ))}
                    {ev.length > 3 && <div className="text-[9px] text-gray-400">+{ev.length - 3} more</div>}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Agenda list */}
      <div className="mt-6 bg-white border border-gray-200 rounded-xl p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-3">Upcoming</div>
        <div className="space-y-2">
          {events.slice(0, 15).map((e) => (
            <div key={e.id} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2">
              <div>
                <div className="font-semibold">{e.title}</div>
                <div className="text-xs text-gray-500">
                  {new Date(e.start).toLocaleString()} · {e.event_type}
                  {e.location && ` · ${e.location}`}
                </div>
              </div>
              {canEdit && (
                <button onClick={() => remove(e.id)} className="text-gray-400 hover:text-red-600" data-testid={`cal-del-${e.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
          {events.length === 0 && <div className="text-xs text-gray-400 text-center py-4">No events this month</div>}
        </div>
      </div>

      {formOpen && <EventForm onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} />}
    </div>
  );
}

function EventForm({ onClose, onSaved }) {
  const nowLocal = new Date();
  nowLocal.setMinutes(0, 0, 0);
  const later = new Date(nowLocal); later.setHours(later.getHours() + 1);
  const toLocalInput = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

  const [f, setF] = useState({
    title: "", event_type: "showing", start: toLocalInput(nowLocal), end: toLocalInput(later),
    location: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/crm/events", {
        title: f.title, event_type: f.event_type,
        start: new Date(f.start).toISOString(), end: new Date(f.end).toISOString(),
        location: f.location || null, notes: f.notes || null,
      }, { withCredentials: true });
      toast.success("Event created");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-8 space-y-3" data-testid="crm-event-form">
        <div className="font-display text-2xl font-semibold">New event</div>
        <Field label="Title*" value={f.title} onChange={(v) => setF({ ...f, title: v })} testId="ef-title" />
        <Select label="Type" value={f.event_type} onChange={(v) => setF({ ...f, event_type: v })} options={EVENT_TYPES} testId="ef-type" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start" type="datetime-local" value={f.start} onChange={(v) => setF({ ...f, start: v })} testId="ef-start" />
          <Field label="End" type="datetime-local" value={f.end} onChange={(v) => setF({ ...f, end: v })} testId="ef-end" />
        </div>
        <Field label="Location" value={f.location} onChange={(v) => setF({ ...f, location: v })} testId="ef-loc" />
        <TextArea label="Notes" value={f.notes} onChange={(v) => setF({ ...f, notes: v })} testId="ef-notes" />
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-500">Cancel</button>
          <button type="submit" disabled={saving || !f.title} className="bg-[#E63946] hover:bg-[#c9313d] text-white px-5 py-2 rounded-md font-semibold text-sm disabled:opacity-60" data-testid="ef-submit">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------- Documents ----------
function DocumentsView({ role }) {
  const [docs, setDocs] = useState([]);
  const canEdit = ["admin", "agent", "loan_officer"].includes(role);
  const load = async () => {
    try {
      const r = await api.get("/crm/documents", { withCredentials: true });
      setDocs(r.data);
    } catch { toast.error("Failed to load documents"); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/crm/documents", fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch { toast.error("Upload failed"); }
    e.target.value = "";
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try { await api.delete(`/crm/documents/${id}`, { withCredentials: true }); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="crm-documents">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl font-semibold">Documents</h2>
        {canEdit && (
          <label className="bg-[#E63946] hover:bg-[#c9313d] text-white px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2 cursor-pointer" data-testid="crm-doc-upload-global">
            <Plus className="w-4 h-4" /> Upload
            <input type="file" onChange={upload} className="hidden" />
          </label>
        )}
      </div>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-100 text-[10px] uppercase tracking-[0.18em] text-gray-500">
            <tr>
              <th className="text-left px-5 py-3">File</th>
              <th className="text-left px-5 py-3">Size</th>
              <th className="text-left px-5 py-3">Category</th>
              <th className="text-left px-5 py-3">Uploaded</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {docs.map((d) => (
              <tr key={d.id} className="hover:bg-stone-50">
                <td className="px-5 py-3 font-medium">
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/crm/documents/${d.id}`} target="_blank" rel="noopener noreferrer" className="text-[#1A1A1A] hover:text-[#E63946] flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5" /> {d.filename}
                    <ExternalLink className="w-3 h-3 opacity-40" />
                  </a>
                </td>
                <td className="px-5 py-3 text-xs text-gray-500">{Math.ceil((d.size || 0) / 1024)} KB</td>
                <td className="px-5 py-3 text-xs">{d.category || "—"}</td>
                <td className="px-5 py-3 text-xs text-gray-500">{d.created_at ? new Date(d.created_at).toLocaleString() : "—"}</td>
                <td className="px-5 py-3 text-right">
                  {canEdit && (
                    <button onClick={() => remove(d.id)} className="text-gray-400 hover:text-red-600" data-testid={`crm-doc-del-${d.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {docs.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-gray-500">No documents.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Team (admin-only) ----------
function TeamView() {
  const [team, setTeam] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const load = async () => {
    try { const r = await api.get("/crm/team", { withCredentials: true }); setTeam(r.data); }
    catch { toast.error("Failed to load team"); }
  };
  useEffect(() => { load(); }, []);
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}?`)) return;
    try { await api.delete(`/crm/team/${u.user_id}`, { withCredentials: true }); toast.success("Removed"); load(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div data-testid="crm-team">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl font-semibold">Team</h2>
        <button onClick={() => setFormOpen(true)} className="bg-[#E63946] hover:bg-[#c9313d] text-white px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2" data-testid="crm-invite-team"><Plus className="w-4 h-4" /> Invite</button>
      </div>
      <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        Add someone by their Google account email. They&apos;ll gain their CRM role the first time they sign in via <code>/admin/login</code> with that Google account.
      </div>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-100 text-[10px] uppercase tracking-[0.18em] text-gray-500">
            <tr><th className="text-left px-5 py-3">Name</th><th className="text-left px-5 py-3">Email</th><th className="text-left px-5 py-3">Role</th><th className="text-left px-5 py-3">Department</th><th className="text-left px-5 py-3">Active</th><th></th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {team.map((u) => (
              <tr key={u.user_id}>
                <td className="px-5 py-3 font-medium">{u.name}</td>
                <td className="px-5 py-3 text-xs text-gray-500">{u.email}</td>
                <td className="px-5 py-3 text-xs uppercase tracking-wide">{u.role}</td>
                <td className="px-5 py-3 text-xs">{u.department}</td>
                <td className="px-5 py-3 text-xs">{u.active ? "✓" : "—"}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => remove(u)} className="text-gray-400 hover:text-red-600" data-testid={`crm-team-del-${u.user_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
            {team.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-gray-500">No team members yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {formOpen && <TeamForm onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} />}
    </div>
  );
}

function TeamForm({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", email: "", role: "agent", department: "realtor", active: true });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/crm/team", f, { withCredentials: true });
      toast.success("Team member added");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-8 space-y-3" data-testid="crm-team-form">
        <div className="font-display text-2xl font-semibold">Invite team member</div>
        <Field label="Full name*" value={f.name} onChange={(v) => setF({ ...f, name: v })} testId="tf-name" />
        <Field label="Email (Google)*" value={f.email} onChange={(v) => setF({ ...f, email: v })} testId="tf-email" />
        <div className="grid grid-cols-2 gap-3">
          <Select label="Role" value={f.role} onChange={(v) => setF({ ...f, role: v })} options={["admin", "agent", "loan_officer", "assistant"]} testId="tf-role" />
          <Select label="Department" value={f.department} onChange={(v) => setF({ ...f, department: v })} options={["realtor", "mortgage", "both"]} testId="tf-dept" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-500">Cancel</button>
          <button type="submit" disabled={saving || !f.name || !f.email} className="bg-[#E63946] hover:bg-[#c9313d] text-white px-5 py-2 rounded-md font-semibold text-sm disabled:opacity-60" data-testid="tf-submit">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------- Shared field helpers ----------
function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.18em] text-gray-500 mb-1">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 bg-stone-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#E63946]" data-testid={testId} />
    </label>
  );
}
function Select({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.18em] text-gray-500 mb-1">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 bg-stone-50 border border-gray-200 rounded-lg text-sm" data-testid={testId}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
function TextArea({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.18em] text-gray-500 mb-1">{label}</span>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full px-3 py-2 bg-stone-50 border border-gray-200 rounded-lg text-sm" data-testid={testId} />
    </label>
  );
}
